import { useState, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { PencilIcon, EyeIcon, EllipsisVerticalIcon, PlusIcon, TrashIcon, DocumentDuplicateIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
// El MISMO `Table` que pinta el listado de documentos y el de MasterCrud, no una
// grilla propia. La de antes era TanStack + virtualizador y por eso divergía en
// todo lo que no se copiara a mano: el hover con acento, el reparto de anchos y el
// paginador. Se pierde la virtualización, y a cambio la paginación acota el número
// de filas montadas —que es lo que la virtualización venía a resolver.
import { Table } from '../Table';
import type { TableColumn } from '../Table/Table.types';
import { LineContextMenu } from './LineContextMenu';
import { LineFulfillmentBadge } from './LineFulfillmentBadge';
import { Loading } from './components/loading';
import { useMasterDocumentTranslation } from './i18n';
import { ROW_ACTION_BTN } from './rowActionStyles';

/**
 * Filas por página por defecto y suelo de alto de la tabla.
 *
 * Con una o dos líneas la caja quedaba tan baja que no se leía como una tabla —
 * cabecera, una fila y el paginador pegados—. Reservar el alto de cuatro filas le da
 * cuerpo sin inventar filas vacías: el hueco es del contenedor, no de la grilla.
 */
const FILAS_POR_DEFECTO = 4;
/**
 * Suelo de alto de la caja de filas: la cabecera de columnas MÁS cuatro filas, a 44px
 * cada una (la densidad `dense` del canon). Se cuenta la cabecera porque vive DENTRO
 * del mismo `div[role="table"]` al que se aplica el suelo. El paginador queda fuera y
 * suma su propio alto, que es lo correcto — el suelo es de la tabla, no de la caja.
 */
const ALTO_MINIMO_TABLA = (FILAS_POR_DEFECTO + 1) * 44;

/**
 * Suelo de ancho de la columna «Acciones»: lo que ocupa su rótulo sin recortarse.
 *
 * Son los 100px del caso de dos botones, que ya es el ancho del canon, y cubren con
 * holgura tanto «Acciones» como «Actions». Va como constante y no medido en tiempo
 * de ejecución a propósito: medir texto obliga a un render intermedio y a leer del
 * layout en cada cambio de datos, y el margen que sobra aquí es de sobra suficiente.
 */
const ANCHO_MINIMO_ACCIONES = 100;

/**
 * Por debajo de este ancho, una columna de datos NO se estira para repartirse el
 * espacio sobrante: se queda en lo que declara.
 *
 * Una columna declarada así de estrecha es, por construcción, de código, consecutivo
 * o unidad de medida — a una columna pensada para texto nadie le pone 60px. Sin esto
 * el `#` crecía igual que «Artículo» en cuanto había hueco, y una tabla con pocas
 * columnas acababa con el consecutivo tan ancho como la descripción.
 *
 * La plantilla puede saltárselo declarando `fixedWidth` en su columna.
 */
const ANCHO_COLUMNA_UTILITARIA = 100;

/** Casilla de selección de fila, con el foco del canon. */
const CHECKBOX_CLS = clsx(
  'w-4 h-4 rounded border-border-primary accent-primary-custom-600',
  'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
  'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
);
import { Button } from '../Button';
import { useMasterDocumentFormatters } from './formatters';
import type {
  LinesSectionProps,
  DocumentLineData,
  LineColumnConfig,
  Breakpoint,
  LineFulfillmentStatus,
} from './MasterDocument.types';

// ─── Helpers ───────────────────────────────────────────────

function shouldHideColumn(hideOn: Breakpoint[] | undefined, current?: Breakpoint): boolean {
  if (!hideOn || !current) return false;
  return hideOn.includes(current);
}

function formatCellValue(
  value: unknown,
  format: string | undefined,
  formatCurrencyCompact: (value: number) => string,
  formatNumber: (value: number) => string,
): string {
  if (value == null) return '—';
  switch (format) {
    case 'currency':
      return formatCurrencyCompact(Number(value));
    case 'number':
      return formatNumber(Number(value));
    case 'percentage':
      return `${Number(value).toFixed(2)}%`;
    case 'date':
      return String(value);
    default:
      return String(value);
  }
}

function isMobileBreakpoint(bp?: Breakpoint): boolean {
  if (!bp) return false;
  return bp === 'xs' || bp === 'sm';
}

function isTerminalStatus(status?: LineFulfillmentStatus): boolean {
  return status === 'Fulfilled' || status === 'Closed';
}

// ─── Line Card (mobile view) ───────────────────────────────

function LineCard({
  line,
  columns,
  onEdit,
  onView,
  onMenu,
  isActive,
  lineEditable = true,
  selectionMode,
  isSelected,
  isDisabled,
  onSelect,
  status,
  selectionError,
  isBusy = false,
}: {
  line: DocumentLineData;
  columns: LineColumnConfig[];
  onEdit: () => void;
  onView?: () => void;
  onMenu: (e: React.MouseEvent) => void;
  isActive: boolean;
  lineEditable?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  onSelect?: () => void;
  status?: LineFulfillmentStatus;
  selectionError?: string;
  /** Mutación en vuelo sobre esta línea. */
  isBusy?: boolean;
}) {
  const { t } = useMasterDocumentTranslation();
  const { formatCurrencyCompact, formatNumber } = useMasterDocumentFormatters();
  const cardColumns = columns.filter((c) => c.showInCard !== false);

  return (
    <div
      className={clsx(
        'rounded-xl bg-bg-primary p-4',
        'shadow-[0_1px_3px_rgba(60,64,67,.15),0_1px_2px_rgba(60,64,67,.10)]',
        'transition-all duration-150',
        isActive && 'ring-2 ring-primary-600',
        'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {selectionMode && (
            <div className="flex flex-col">
              <input
                type="checkbox"
                checked={!!isSelected}
                disabled={isDisabled}
                onChange={onSelect}
                className={clsx(
                  'w-4 h-4 rounded border-border-primary accent-primary-custom-600',
                  'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
                  'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
                  isDisabled && 'opacity-50 cursor-not-allowed',
                )}
                aria-label={t('master_document.lines.selectLine')}
              />
              {selectionError && (
                <span className="text-xs text-red-600 dark:text-red-400 mt-0.5 whitespace-nowrap">
                  {selectionError}
                </span>
              )}
            </div>
          )}
          <span className="text-xs font-bold text-content-secondary dark:text-dark-content-secondary tabular-nums">
            #{line.line_number}
          </span>
          {selectionMode && status && (
            <LineFulfillmentBadge status={status} />
          )}
        </div>
        <div className="flex items-center gap-1">
          {isBusy ? (
            // Misma decisión que en la tabla: el indicador SUSTITUYE las acciones
            // de la fila. En tarjeta ya son visibles siempre, así que aquí basta
            // con ocupar su sitio.
            <span className="p-1.5">
              <Loading size="sm" layout="inline" ariaLabel={t('master_document.chrome.updating')} />
            </span>
          ) : lineEditable ? (
            <button
              type="button"
              onClick={onEdit}
              className="p-1.5 rounded-md hover:bg-primary-100 dark:hover:bg-dark-bg-secondary text-content-tertiary transition-colors focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary"
              aria-label={t('master_document.lines.editLine')}
            >
              <PencilIcon className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          ) : (
            onView && (
              <button
                type="button"
                onClick={onView}
                className="p-1.5 rounded-md hover:bg-primary-100 dark:hover:bg-dark-bg-secondary text-content-tertiary transition-colors focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary"
                // Misma condición que la fila de escritorio y que el título del
                // modal: si abre en consulta, se anuncia como consulta.
                aria-label={t('master_document.lines.viewLine')}
              >
                <EyeIcon className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            )
          )}
          {!isBusy && (
            <button
              type="button"
              onClick={onMenu}
              className="p-1.5 rounded-md hover:bg-primary-100 dark:hover:bg-dark-bg-secondary text-content-tertiary transition-colors focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary"
              aria-label={t('master_document.lines.moreOptions')}
            >
              <EllipsisVerticalIcon className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {cardColumns.map((col) => (
          <div key={col.key} className="flex flex-col">
            <span className="text-xs font-bold uppercase text-content-secondary dark:text-dark-content-secondary tracking-wide">
              {col.header}
            </span>
            <span
              className={clsx(
                'text-xs font-medium text-content-primary',
                ['currency', 'number', 'percentage', 'date'].includes(col.format ?? '') && 'tabular-nums',
                col.align === 'right' && 'text-right',
                'dark:text-dark-content-primary',
              )}
            >
              {formatCellValue(line[col.key], col.format, formatCurrencyCompact, formatNumber)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LinesSection ──────────────────────────────────────────

/**
 * Sección de líneas de detalle del documento maestro.
 *
 * Renderiza una tabla virtualizada (TanStack Table + TanStack Virtual) en
 * escritorio y tarjetas apiladas en móvil. Incluye menú contextual por línea
 * (editar, duplicar, eliminar), estado activo visual y estado vacío.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - LinesSectionProps, DocumentLineData, LineColumnConfig
 *
 * @example
 * ```tsx
 * <LinesSection
 *   title="Líneas del documento"
 *   lines={documentLines}
 *   columns={lineColumns}
 *   onEditLine={(id) => openLineModal(id)}
 *   onDeleteLine={(id) => removeLine(id)}
 *   canAddLine={isEditable}
 *   onAddLine={() => openNewLineModal()}
 * />
 * ```
 */
export function LinesSection({
  title,
  lines,
  columns: columnConfigs,
  alerts,
  canAddLine,
  onAddLine,
  onEditLine,
  onViewLine,
  onDeleteLine,
  lineDeletable = true,
  onDuplicateLine,
  lineEditable = true,
  activeLineId,
  busyLineId,
  currentBreakpoint,
  className,
  selectionMode = false,
  selectedLineIds = [],
  onLineSelect,
  lineStatuses,
  lineSelectionErrors,
  onOpenLineCloseModal,
  lineActions,
  getLineActions,
  hideTitle = false,
}: LinesSectionProps) {
  const { t } = useMasterDocumentTranslation();
  const { formatCurrencyCompact, formatNumber } = useMasterDocumentFormatters();
  const [contextMenu, setContextMenu] = useState<{
    open: boolean;
    position: { x: number; y: number };
    lineId: string;
    /** Abierto desde la celda de la fila (muestra sólo el desborde) o desde la tarjeta. */
    desdeFila?: boolean;
  }>({ open: false, position: { x: 0, y: 0 }, lineId: '' });

  // Header de la tabla desktop: vive fuera del scroll vertical del cuerpo (para
  // quedar fijo al hacer scroll vertical), pero su scroll HORIZONTAL se sincroniza
  // con el del cuerpo para que columnas y encabezados se desplacen juntos.
  const isMobile = isMobileBreakpoint(currentBreakpoint);

  // Filter out hidden columns
  const visibleColumns = useMemo(
    () => columnConfigs.filter((c) => !shouldHideColumn(c.hideOn, currentBreakpoint)),
    [columnConfigs, currentBreakpoint],
  );

  // TanStack table columns

  /**
   * Acciones de UNA línea, en el orden del canon. Antes esto era el contenido del
   * menú contextual y además había un botón «Ver/Editar» suelto en la celda, así que
   * la MISMA acción aparecía dos veces: como botón y como primer ítem del menú. Ahora
   * es una sola lista y la celda decide cuáles se pintan de frente.
   */
  const construirAccionesDeLinea = useCallback((lineaId: string) => {
    const items: Array<{
      label: string;
      icon?: React.ReactNode;
      onClick: () => void;
      danger?: boolean;
      separator?: boolean;
      disabled?: boolean;
    }> = [];

    // El rótulo y el icono siguen la MISMA condición que el título del modal que van
    // a abrir (`MasterDocument`: `readOnly ? 'Ver línea' : 'Editar línea'`), y no una
    // propia. Un intento anterior unificó las dos ramas en «Editar» con lápiz
    // razonando que el destino es el mismo; lo es, pero el DESENLACE no, y eso es lo
    // que el usuario decide al pulsar: no «a dónde voy» sino «¿puedo cambiar esto?».
    // Con el lápiz esa respuesta sólo llega DESPUÉS de abrir el modal, campo por
    // campo deshabilitado — en una grilla de 40 líneas de un documento ya
    // contabilizado, 40 aperturas para averiguar lo que un icono decía de una.
    //
    // Además el propio kit ya distingue las dos puertas en el listado de documentos
    // (ojo «Ver detalle» / lápiz «Editar»), así que mantener aquí un solo icono hacía
    // que el mismo usuario viera dos criterios para la misma semántica.
    if (lineEditable) {
      items.push({
        label: t('master_document.lines.edit'),
        onClick: () => onEditLine(lineaId),
        icon: <PencilIcon className="w-4 h-4" aria-hidden="true" />,
      });
    } else if (onViewLine) {
      items.push({
        label: t('master_document.lines.view'),
        onClick: () => onViewLine(lineaId),
        icon: <EyeIcon className="w-4 h-4" aria-hidden="true" />,
      });
    }

    if (onDuplicateLine) {
      items.push({
        label: t('master_document.lines.duplicate'),
        onClick: () => onDuplicateLine(lineaId),
        icon: <DocumentDuplicateIcon className="w-4 h-4" aria-hidden="true" />,
      });
    }

    if (onOpenLineCloseModal) {
      const lineStatus = lineStatuses?.[lineaId];
      const lineDesc = String(lines.find((l) => l.id === lineaId)?.description ?? lineaId);
      if (lineActions?.close && lineStatus !== 'Closed') {
        items.push({
          label: t('master_document.lines.closeLine'),
          onClick: () => onOpenLineCloseModal(lineaId, lineDesc, 'close'),
          icon: <LockClosedIcon className="w-4 h-4" aria-hidden="true" />,
          separator: true,
        });
      }
      if (lineActions?.reopen && lineStatus === 'Closed') {
        items.push({
          label: t('master_document.lines.reopenLine'),
          onClick: () => onOpenLineCloseModal(lineaId, lineDesc, 'reopen'),
          icon: <LockOpenIcon className="w-4 h-4" aria-hidden="true" />,
          separator: true,
        });
      }
    }

    // Acciones de línea ADICIONALES (declarativas): se anexan como un bloque
    // separado, tras las quemadas y antes de "Eliminar". Aditivo: si no se provee
    // `getLineActions` o no devuelve nada, el menú es idéntico al actual.
    const line = lines.find((l) => l.id === lineaId);
    const extraActions = line ? (getLineActions?.(line) ?? []) : [];
    extraActions.forEach((action, idx) => {
      items.push({
        label: action.label,
        icon: action.icon,
        onClick: () => action.onClick(line as Record<string, unknown>),
        danger: action.variant === 'danger',
        disabled: action.disabled,
        // Separador solo en la primera para delimitar el bloque extra.
        separator: idx === 0,
      });
    });

    // AC-1: con `lineDeletable === false` no se ofrece NINGUNA afordancia de
    // borrado —ni botón de fila ni ítem de menú—, que es lo que pide una pestaña de
    // sólo consulta. «Deshabilitada» no sirve: la acción no debe ofrecerse.
    //
    // Sin `icon` la celda caía al `EllipsisVerticalIcon` de reserva, así que
    // «Eliminar» se pintaba con el icono de «más opciones»: el botón anunciaba un
    // menú y borraba una línea.
    if (lineDeletable && onDeleteLine) {
      items.push({
        label: t('master_document.lines.delete'),
        onClick: () => onDeleteLine(lineaId),
        icon: <TrashIcon className="w-4 h-4" aria-hidden="true" />,
        danger: true,
        separator: true,
      });
    }

    return items;
  }, [lines, lineStatuses, lineActions, lineEditable, lineDeletable, onEditLine, onViewLine, onDuplicateLine, onDeleteLine, onOpenLineCloseModal, getLineActions, t]);

  /** Reparto del canon: hasta 3 de frente; de 4 en adelante, 2 + «Más opciones». */
  const MAX_DIRECTAS_LINEA = 3;
  const repartir = useCallback((lineaId: string) => {
    const todas = construirAccionesDeLinea(lineaId);
    return todas.length <= MAX_DIRECTAS_LINEA
      ? { directas: todas, enMenu: [] as typeof todas }
      : { directas: todas.slice(0, 2), enMenu: todas.slice(2) };
  }, [construirAccionesDeLinea]);

  /**
   * Ancho de la columna «Acciones», calculado a partir de los botones que de verdad
   * lleva. Estaba fijo en el ancho de tres y con dos controles sobraba un hueco que
   * no explicaba nada. La fórmula es la del canon: n botones de 28px con `gap-3`,
   * más los 16/16 de padding de celda.
   */
  const anchoColumnaAcciones = useMemo(() => {
    let max = 1;
    lines.forEach((l) => {
      const { directas, enMenu } = repartir(String(l.id));
      max = Math.max(max, directas.length + (enMenu.length > 0 ? 1 : 0));
    });
    // Los botones más el padding de celda… pero NUNCA por debajo del ancho que
    // necesita el propio rótulo. Con un solo botón la fórmula daba 60px y
    // «Acciones» se recortaba a la mitad (medido en dev: 46px de texto en 28px de
    // hueco). Una cabecera que no se lee no nombra su columna.
    return Math.max(max * 28 + (max - 1) * 12 + 32, ANCHO_MINIMO_ACCIONES);
  }, [lines, repartir]);

  const tableColumns = useMemo<TableColumn<DocumentLineData>[]>(() => {
    const cols: TableColumn<DocumentLineData>[] = [];

    // Columna de selección
    if (selectionMode) {
      const nonTerminalLines = lines.filter((l) => !isTerminalStatus(lineStatuses?.[l.id]));
      const nonTerminalSelected = nonTerminalLines.filter((l) => selectedLineIds.includes(l.id));
      const allSelected = nonTerminalLines.length > 0 && nonTerminalSelected.length === nonTerminalLines.length;
      const someSelected = nonTerminalSelected.length > 0 && nonTerminalSelected.length < nonTerminalLines.length;

      cols.push({
        header: (
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => { if (el) el.indeterminate = someSelected; }}
            onChange={(e) => { nonTerminalLines.forEach((l) => onLineSelect?.(l.id, e.target.checked)); }}
            className={CHECKBOX_CLS}
            aria-label={t('master_document.lines.selectAllLines')}
          />
        ),
        headerRaw: true,
        accessor: (row) => row.id,
        // Utilitaria: NO reparte el espacio sobrante (§E.5).
        width: '44px',
        fixedWidth: true,
        render: (_v, row) => {
          const status = lineStatuses?.[row.id];
          const disabled = isTerminalStatus(status);
          const isSelected = selectedLineIds.includes(row.id);
          const error = lineSelectionErrors?.[row.id];
          return (
            <div className="flex flex-col items-start" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={isSelected}
                disabled={disabled}
                onChange={() => onLineSelect?.(row.id, !isSelected)}
                className={clsx(CHECKBOX_CLS, disabled && 'opacity-50 cursor-not-allowed')}
                aria-label={t('master_document.lines.selectLine')}
              />
              {error && (
                <span className="text-xs text-red-600 dark:text-red-400 mt-0.5 whitespace-nowrap">{error}</span>
              )}
            </div>
          );
        },
      });
    }

    // Columnas de datos. SIN `fixedWidth`: el `width` se convierte en
    // `minmax(width, 1fr)` y son ellas las que se reparten el ancho sobrante, que es
    // como lo hace MasterCrud. Antes cada columna iba a `flex: 0 0 Npx` y ninguna
    // crecía, así que el sobrante se quedaba pegado a la última —la de acciones— y
    // parecía una columna enorme.
    visibleColumns.forEach((col) => {
      const anchoDeclarado = col.width ? parseInt(col.width, 10) : NaN;
      // `fixedWidth` explícito de la plantilla manda; si no lo hay, se deduce del
      // ancho declarado (ver `ANCHO_COLUMNA_UTILITARIA`).
      const noSeEstira = col.fixedWidth ?? (Number.isFinite(anchoDeclarado) && anchoDeclarado < ANCHO_COLUMNA_UTILITARIA);
      cols.push({
        header: col.header,
        accessor: (row) => row[col.key],
        width: col.width,
        fixedWidth: noSeEstira,
        align: col.align,
        render: (value) => {
          const isMono = ['currency', 'number', 'percentage', 'date'].includes(col.format ?? '');
          return (
            <span className={clsx('truncate', isMono && 'tabular-nums font-medium')}>
              {formatCellValue(value, col.format, formatCurrencyCompact, formatNumber)}
            </span>
          );
        },
      });
    });

    // Badge de cumplimiento (modo selección)
    if (selectionMode) {
      cols.push({
        header: t('master_document.crossing.pending.state'),
        accessor: (row) => row.id,
        width: '100px',
        fixedWidth: true,
        render: (_v, row) => {
          const status = lineStatuses?.[row.id];
          return status ? <LineFulfillmentBadge status={status} /> : null;
        },
      });
    }

    // Columna de acciones: ancho exacto de sus botones y FIJA — no crece.
    cols.push({
      header: t('master_document.list.rowActions'),
      accessor: (row) => row.id,
      width: `${anchoColumnaAcciones}px`,
      fixedWidth: true,
      stickyRight: true,
      align: 'center',
      render: (_v, row) => {
        const lineId = String(row.id);
        if (lineId === busyLineId) {
          return (
            // El indicador conserva la caja de 28px del botón al que sustituye:
            // un spinner desnudo encogería esa fila y sólo esa.
            <div className="flex items-center justify-center">
              <button type="button" disabled className={ROW_ACTION_BTN}>
                <Loading size="sm" layout="inline" ariaLabel={t('master_document.chrome.updating')} />
              </button>
            </div>
          );
        }

        const { directas, enMenu } = repartir(lineId);
        if (directas.length === 0 && enMenu.length === 0) return null;

        return (
          // Siempre visibles y con la separación del canon, igual que en el listado
          // de documentos: una acción que sólo aparece al pasar el ratón no se
          // alcanza con el teclado ni con el dedo.
          <div className="flex items-center gap-3 justify-center" onClick={(e) => e.stopPropagation()}>
            {directas.map((a) => (
              <button
                key={a.label}
                type="button"
                title={a.label}
                aria-label={a.label}
                disabled={a.disabled}
                onClick={(e) => { e.stopPropagation(); a.onClick(); }}
                className={clsx(ROW_ACTION_BTN, a.danger && '!text-error-content !border-error-border hover:!bg-error-bg')}
              >
                <span className="w-[15px] h-[15px] inline-flex items-center justify-center">
                  {a.icon ?? <EllipsisVerticalIcon className="w-full h-full" aria-hidden="true" />}
                </span>
              </button>
            ))}

            {enMenu.length > 0 && (
              <button
                type="button"
                title={t('master_document.lines.moreLineOptions')}
                aria-label={t('master_document.lines.moreLineOptions')}
                aria-haspopup="menu"
                onClick={(e) => {
                  e.stopPropagation();
                  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setContextMenu({ open: true, position: { x: r.right, y: r.bottom }, lineId, desdeFila: true });
                }}
                className={ROW_ACTION_BTN}
              >
                <EllipsisVerticalIcon className="w-[15px] h-[15px]" aria-hidden="true" />
              </button>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [visibleColumns, busyLineId, selectionMode, selectedLineIds, lineStatuses, lineSelectionErrors, onLineSelect, lines, t, formatCurrencyCompact, formatNumber, repartir, anchoColumnaAcciones]);

  // Paginación en cliente: las líneas ya están enteras en memoria, así que el
  // paginador no pide nada, sólo recorta. Es lo que hace falta para que la caja se
  // vea y se maneje como la del listado.
  const [pagina, setPagina] = useState(1);
  // Cuatro por defecto, no veinticinco: es el tamaño con el que la caja se ve
  // proporcionada dentro de la tarjeta del detalle sin empujar el resto fuera de
  // pantalla, y coincide con el alto mínimo que la tabla reserva (ver `MIN_FILAS`).
  const [tamanoPagina, setTamanoPagina] = useState(FILAS_POR_DEFECTO);
  const totalPaginas = Math.max(1, Math.ceil(lines.length / tamanoPagina));
  // Si se borran líneas y la página actual deja de existir, hay que volver a una que
  // sí: sin esto la tabla se queda en blanco con datos detrás.
  const paginaSegura = Math.min(pagina, totalPaginas);
  const lineasDeLaPagina = useMemo(
    () => lines.slice((paginaSegura - 1) * tamanoPagina, paginaSegura * tamanoPagina),
    [lines, paginaSegura, tamanoPagina],
  );
  // ¿La barra de la sección aporta algo? El contador ya NO cuenta: lo dice el
  // paginador. Sin título, sin avisos y sin botón de añadir, la barra estaba vacía.
  const mostrarBarra = (!hideTitle && !!title) || (alerts?.length ?? 0) > 0 || canAddLine;

  const paginacion = {
    currentPage: paginaSegura,
    totalPages: totalPaginas,
    onPageChange: setPagina,
    pageSize: tamanoPagina,
    pageSizeOptions: [4, 10, 25, 50, 100],
    onPageSizeChange: (n: number) => { setTamanoPagina(n); setPagina(1); },
    totalRecords: lines.length,
    previousLabel: t('master_document.list.previousPage'),
    nextLabel: t('master_document.list.nextPage'),
    perPageLabel: t('master_document.list.perPage'),
    recordsLabel: t('master_document.list.records'),
    goToPageLabel: t('master_document.list.goToPage'),
  };


  // El menú contextual muestra SOLO lo que no cupo de frente cuando se abre desde la
  // fila; desde la tarjeta (móvil), donde no hay botones, muestra todo.
  const contextMenuItems = useMemo(() => {
    if (!contextMenu.lineId) return [];
    const { enMenu } = repartir(contextMenu.lineId);
    return contextMenu.desdeFila ? enMenu : construirAccionesDeLinea(contextMenu.lineId);
  }, [contextMenu.lineId, contextMenu.desdeFila, repartir, construirAccionesDeLinea]);

  const handleMenuForCard = useCallback(
    (lineId: string) => (e: React.MouseEvent) => {
      setContextMenu({
        open: true,
        position: { x: e.clientX, y: e.clientY },
        lineId,
        desdeFila: false,
      });
    },
    [],
  );

  return (
    <div
      className={clsx(
        // Caja del canon: la MISMA que envuelve la tabla del listado de
        // MasterCrud — borde de 1px y `rounded-xl`, sin sombra de tarjeta. La
        // sombra pertenecía a la época en que esto flotaba sobre un fondo gris;
        // dentro de la tarjeta del detalle sólo añadía relieve sobre relieve.
        'bg-white dark:bg-dark-bg-primary rounded-xl overflow-hidden',
        'border border-border-primary dark:border-dark-border-primary',
        className,
      )}
    >
      {/* Barra de la sección. Se pinta SÓLO si lleva algo que el paginador no diga
          ya: un título, un aviso o el botón de añadir. Con las pestañas activas y el
          documento en sólo lectura no quedaba nada dentro salvo el contador de
          líneas —que el paginador del `Table` muestra abajo—, así que era una franja
          de 53px que repetía un dato y separaba la caja de su propia cabecera.

          Va en blanco: en gris quedaba pegada a la banda gris de los encabezados y
          se leían como una sola franja de dos tonos. */}
      {mostrarBarra && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-dark-bg-primary border-b border-border-primary dark:border-dark-border-primary">
          <div className="flex items-center gap-2 min-w-0">
            {!hideTitle && (
              <h3 className="text-sm font-bold text-content-primary dark:text-dark-content-primary">
                {title}
              </h3>
            )}
            {alerts?.map((alert, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold"
                style={{ backgroundColor: alert.bg, color: alert.color }}
              >
                {alert.label}
              </span>
            ))}
          </div>

          {canAddLine && (
            // El MISMO `Button` que el «Nuevo» del listado, no una réplica a mano: la
            // copia se había quedado en 12px y `py-1.5` mientras el canon iba por 14px.
            <Button
              type="default"
              size="base"
              className="!text-sm font-medium"
              leftIcon={<PlusIcon className="w-4 h-4" />}
              onClick={onAddLine}
            >
              {t('master_document.lines.newLine')}
            </Button>
          )}
        </div>
      )}

      {/* Mobile card view */}
      {isMobile ? (
        <div className="flex flex-col gap-3 p-4">
          {lines.length === 0 ? (
            <EmptyLines />
          ) : (
            lines.map((line) => (
              <LineCard
                key={line.id}
                line={line}
                columns={columnConfigs}
                onEdit={() => onEditLine(line.id)}
                onView={onViewLine ? () => onViewLine(line.id) : undefined}
                onMenu={handleMenuForCard(line.id)}
                isActive={line.id === activeLineId}
                lineEditable={lineEditable}
                selectionMode={selectionMode}
                isSelected={selectedLineIds.includes(line.id)}
                isDisabled={isTerminalStatus(lineStatuses?.[line.id])}
                onSelect={() => onLineSelect?.(line.id, !selectedLineIds.includes(line.id))}
                status={lineStatuses?.[line.id]}
                selectionError={lineSelectionErrors?.[line.id]}
                isBusy={line.id === busyLineId}
              />
            ))
          )}
        </div>
      ) : (
        /* Desktop: el `Table` del kit, el mismo del listado de documentos */
        <div className="flex flex-col min-h-0">
          <Table
            columns={tableColumns}
            data={lineasDeLaPagina}
            emptyMessage={t('master_document.lines.emptyTitle')}
            variant="basic"
            showBorder={false}
            showShadow={false}
            showPadding={false}
            fullWidth
            accentHover
            dense
            // La banda gris del canon en la cabecera, la misma del listado de
            // documentos y de MasterCrud. Va por `headerBand` y NO por `stickyHeader`:
            // aquel convierte la tabla en contenedor de scroll, y esta vive dentro de
            // la tarjeta del detalle, que es quien scrollea.
            headerBand
            // El suelo va en la caja de FILAS (`div[role="table"]`), no en este
            // envoltorio: puesto aquí, el sobrante lo absorbía el contenedor del
            // paginador —que es el que crece— y quedaba un paginador altísimo debajo
            // de una tabla igual de estrecha, justo lo contrario de lo buscado.
            minGridHeight={ALTO_MINIMO_TABLA}
            // La fila no navega: la puerta a la línea es su botón de acción, igual
            // que en el listado de documentos.
            pagination={paginacion}
            className="!bg-transparent !shadow-none !rounded-none !p-0"
          />
        </div>
      )}

      {/* Context menu */}
      <LineContextMenu
        open={contextMenu.open}
        position={contextMenu.position}
        onClose={() => setContextMenu((prev) => ({ ...prev, open: false }))}
        items={contextMenuItems}
      />
    </div>
  );
}

// ─── Empty State ───────────────────────────────────────────

function EmptyLines() {
  const { t } = useMasterDocumentTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <svg className="w-12 h-12 text-border-primary dark:text-dark-border-primary mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <p className="text-sm font-medium text-content-secondary dark:text-dark-content-secondary">{t('master_document.lines.emptyTitle')}</p>
      <p className="text-xs text-content-secondary dark:text-dark-content-secondary mt-1">{t('master_document.lines.emptyHint')}</p>
    </div>
  );
}
